import { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { errorMessage, setAuthFailureHandler, setForbiddenHandler } from "../lib/api";
import { shouldRefreshRights } from "../lib/rightsRefresh";
import { setActiveZone, setCompanyZone, userZone } from "../lib/time";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = logged
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    // Registered before the /auth/me call below so a 401 it triggers can go
    // through the refresh-and-retry path in lib/api.js's interceptor.
    setAuthFailureHandler(() => setUser(false));
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch (e) {
        setUser(false);
      } finally {
        setChecking(false);
      }
    })();
    return () => setAuthFailureHandler(null);
  }, []);

  // Rights read at load time only would leave revoked buttons on screen (and granted ones hidden) until a reload.
  // Re-read them when the person comes back to the tab and right after a 403; a deactivated account is already sent to the
  // login page by the 401 path above.
  const signedIn = !!user;
  // Back on the tab: spaced from ANY read (the initial one included). After a 403: spaced only from the previous read caused by a
  // 403, so a refusal right after the page loaded can still bring the interface up to date.
  const lastRightsRead = useRef({ any: null, forbidden: null });
  useEffect(() => {
    if (!signedIn) return undefined;
    lastRightsRead.current = { any: Date.now(), forbidden: null };
    let reading = false;
    const reread = async (reason) => {
      const last = reason === "forbidden" ? lastRightsRead.current.forbidden : lastRightsRead.current.any;
      if (reading || !shouldRefreshRights(reason, Date.now(), last)) return;
      reading = true;
      const now = Date.now();
      lastRightsRead.current = { any: now, forbidden: reason === "forbidden" ? now : lastRightsRead.current.forbidden };
      try {
        const { data } = await api.get("/auth/me");
        setUser((prev) => (prev && JSON.stringify(prev) === JSON.stringify(data) ? prev : data));
      } catch (e) {
        // Keep what is shown: a network error must not sign anybody out (a 401 already goes through the refresh path).
      } finally {
        reading = false;
      }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") reread("visible"); };
    document.addEventListener("visibilitychange", onVisibility);
    setForbiddenHandler(() => reread("forbidden"));
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      setForbiddenHandler(null);
    };
  }, [signedIn]);

  // Every screen that prints an hour reads the zone of the signed-in person (profile, else company, else Dakar). Set while rendering, not in an
  // effect: a page's first render (a form's default « now ») runs BEFORE the effects of its parents and would read the previous zone.
  setActiveZone(user ? userZone(user) : undefined);
  setCompanyZone(user ? user.company_timezone : undefined);

  const updateUser = (patch) => setUser((prev) => (prev ? { ...prev, ...patch } : prev));

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: errorMessage(e, "Connexion impossible. Vérifiez votre réseau puis réessayez.") };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
